/**
 * The score band shared by the scorecard and its detail drawer.
 *
 * It lives apart from both because both need it: the card shows the band as a tag, and the drawer repeats
 * that tag in its header. Leaving it in either one would make the other import its sibling.
 */

// Numeric score bands. The cutoffs are conservative — better to under-promise on strong than to
// let a mediocre dimension render green. See scorecards.md §8 for the source of truth.
export type PostureBand = 'strong' | 'adequate' | 'fair' | 'weak' | 'unknown'

export const toPostureBand = (score: number): PostureBand => {
  if (!Number.isFinite(score)) {
    return 'unknown'
  }
  if (score >= 0.8) {
    return 'strong'
  }
  if (score >= 0.6) {
    return 'adequate'
  }
  if (score >= 0.4) {
    return 'fair'
  }
  return 'weak'
}

/**
 * The scored bands, weakest first — the order a ramp or a key presents them in.
 *
 * One ordering so a legend cannot disagree with the colours beside it: `POSTURE_RAMP` maps its fills
 * through this, and the scale bar maps its labels through it too. `unknown` is absent because it is the
 * absence of a score rather than a point on the scale.
 */
export const POSTURE_BAND_ORDER = ['weak', 'fair', 'adequate', 'strong'] as const satisfies readonly PostureBand[]

export type ScoredPostureBand = (typeof POSTURE_BAND_ORDER)[number]

export const BAND_LABELS: Record<PostureBand, string> = {
  strong: 'Strong',
  adequate: 'Adequate',
  fair: 'Fair',
  weak: 'Weak',
  unknown: 'Unknown',
}

export const formatPosturePercent = (score: number): string =>
  Number.isFinite(score) ? `${Math.round(score * 100)}%` : '—'

/**
 * The tag's full label: the score beside the band it falls in.
 *
 * An unscored dimension shows the band alone. Pairing a placeholder with a separator — "— · Unknown" —
 * reads as though a number were missing rather than never existing.
 */
export const postureTagLabel = (score: number): string => {
  const band = toPostureBand(score)
  if (band === 'unknown') {
    return BAND_LABELS[band]
  }
  return `${formatPosturePercent(score)} · ${BAND_LABELS[band]}`
}
